if (!customElements.get('common-tab-products')) {
  class TabProducts extends HTMLElement {
    constructor() {
      super();
      this.tabList = this.querySelector('.bfcm-tab-products__tab-list');
      this.containerList = this.querySelectorAll('.bfcm-tab-products__products-container');
      this.tabSwiperContainer = this.querySelectorAll('.bfcm-tab-products__tab-wrapper');
      this.tabSwiperScrollbar = this.querySelector('.bfcm-tab-products__scrollbar');
      this.tabSwiper = undefined;
      this.showMoreBtnList = this.querySelectorAll('.bfcm-tab-products__show-more-btn');
      this.tabSwipers = {};
      this.initTabSwiper();
      this.initTabEvent();
    }

    connectedCallback() {
      const startLayout = () => this.initResponsiveProductLayout();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', startLayout, { once: true });
      } else {
        startLayout();
      }
    }

    isMobileSwiperEnabled() {
      return this.classList.contains('enable-swiper-mb') && window.innerWidth <= 850;
    }

    initResponsiveProductLayout() {
      if (this.isMobileSwiperEnabled()) {
        this.initMobileSwiperLayout();
        this.finishShowMoreLayout();
        return;
      }
      this.initShowMoreEvent();
    }

    finishShowMoreLayout() {
      this.classList.remove('is-showmore-pending');
      this.scrollToDeepLinkedTab();
    }

    activateTab(tabName) {
      if (!this.tabList || !tabName) return;

      const items = this.tabList.querySelectorAll('.bfcm-tab-products__tab-item');
      let tabItem = null;
      items.forEach((item) => {
        if (item.getAttribute('data-tab') === tabName) tabItem = item;
      });
      if (!tabItem) return;

      const index = Number(tabItem.getAttribute('data-index')) || 1;
      if (this.tabSwiper && typeof this.tabSwiper.slideTo === 'function') {
        this.tabSwiper.slideTo(index - 1);
      }

      this.tabList.querySelector('.active')?.classList.remove('active');
      tabItem.classList.add('active');
      for (const container of this.containerList) {
        container.classList.toggle('active', container.getAttribute('data-tab') === tabName);
      }

      const swiper = this.tabSwipers[tabName];
      if (swiper && typeof swiper.update === 'function') {
        swiper.update();
        swiper.slideTo(0);
      }
    }

    scrollToDeepLinkedTab() {
      const tabName = this.getAttribute('data-deeplink-tab');
      if (!tabName || this._deepLinkAligned) return;
      this._deepLinkAligned = true;
      this.activateTab(tabName);

      let userMoved = false;
      const markUserMoved = () => {
        userMoved = true;
      };
      window.addEventListener('wheel', markUserMoved, { passive: true, once: true });
      window.addEventListener('touchmove', markUserMoved, { passive: true, once: true });
      window.addEventListener('keydown', markUserMoved, { once: true });

      const align = () => {
        if (userMoved) return;
        const header = document.querySelector('.site-header') || document.querySelector('.header');
        const sticky = document.querySelector('.common-sticky-navigator') || document.querySelector('.section-common-sticky-navigator');
        const offset = (header?.offsetHeight || 0) + (sticky?.offsetHeight || 0) + 20;
        const top = this.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      };

      requestAnimationFrame(() => requestAnimationFrame(align));
      if (document.readyState === 'complete') return;
      window.addEventListener('load', () => requestAnimationFrame(align), { once: true });
    }

    groupCardsIntoSlides(swiperEl) {
      const wrapper = swiperEl.querySelector('.swiper-wrapper');
      if (!wrapper || wrapper.dataset.mbGrouped === 'true') return;

      const cards = Array.from(swiperEl.querySelectorAll('.card-product-4'));
      if (!cards.length) return;

      const slidesFragment = document.createDocumentFragment();
      let group = document.createDocumentFragment();
      let count = 0;

      cards.forEach((card, index) => {
        count += 1;
        group.appendChild(card);
        if (count === 3 || index === cards.length - 1) {
          const slide = document.createElement('div');
          slide.classList.add('swiper-slide');
          slide.appendChild(group);
          slidesFragment.appendChild(slide);
          group = document.createDocumentFragment();
          count = 0;
        }
      });

      wrapper.replaceChildren();
      wrapper.appendChild(slidesFragment);
      wrapper.dataset.mbGrouped = 'true';
    }

    createTabProductSwiper(swiperEl, tabName) {
      if (typeof Swiper === 'undefined' || this.tabSwipers[tabName]) return;

      const options = {
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        watchOverflow: true,
        slidesPerView: 1,
        spaceBetween: 20,
        breakpoints: {
          589: {
            slidesPerView: Number(this.getAttribute('data-swiper-mb-num')) || 1.2,
            spaceBetween: 10
          },
          1024: {
            slidesPerView: Number(this.getAttribute('data-swiper-ipad-num')) || 2,
            spaceBetween: 15
          }
        }
      };

      if (this.getAttribute('data-enable-normalize-slide') === 'true') {
        options.normalizeSlideIndex = false;
      }

      if (this.getAttribute('data-enable-pagination') === 'true') {
        const paginationEl = swiperEl.querySelector('.swiper-pagination');
        if (paginationEl) {
          options.pagination = {
            el: paginationEl,
            clickable: true
          };
          if (this.getAttribute('data-enable-progress-bar') === 'true') {
            options.pagination.type = 'progressbar';
          }
          if (this.getAttribute('data-enable-pagination-progress') === 'true') {
            options.pagination.progressbarFillClass = 'pagination-progressbar-fill';
          }
        }
      }

      if (this.getAttribute('data-enable-scrollbar') === 'true') {
        const scrollbarEl = swiperEl.querySelector('.swiper-scrollbar');
        if (scrollbarEl) {
          options.scrollbar = {
            el: scrollbarEl
          };
        }
      }

      try {
        this.tabSwipers[tabName] = new Swiper(swiperEl, options);
      } catch (error) {
        console.error('Swiper init failed for', tabName, error);
      }
    }

    initMobileSwiperLayout() {
      this.containerList.forEach((tabContainer) => {
        const swiperEl = tabContainer.querySelector('.bfcm-tab-products__products-container-inner.swiper-container');
        if (!swiperEl) return;
        this.groupCardsIntoSlides(swiperEl);
        this.createTabProductSwiper(swiperEl, tabContainer.getAttribute('data-tab'));
      });
    }

    initTabSwiper() {
      this.tabSwiper = new Swiper(this.tabSwiperContainer, {
        slidesPerView: "auto",
        spaceBetween: 0,
        scrollbar: {
          el: this.tabSwiperScrollbar,
        },
      })
    }

    initTabEvent() {
      this.tabList.addEventListener('click', (e) => {
        const target = e.target;
        if (e.target.tagName == 'LI') {
          this.activateTab(target.getAttribute('data-tab'));
          // // Scroll into view
          // const container = this.tabList;
          // const containerLeft = container.scrollLeft;
          // const containerWidth = container.clientWidth;
          // const itemLeft = target.offsetLeft - container.offsetLeft;
          // const itemWidth = target.offsetWidth;
          // // console.log(containerLeft, containerWidth, itemLeft, itemWidth);
          // if (itemLeft < containerLeft || itemLeft + itemWidth > containerLeft + containerWidth) {
          //   container.scroll({
          //     left: itemLeft,
          //     behavior: 'smooth'
          //   });
          // }
        }
      });
    }

    initShowMoreEvent() {
      const columnsPerRow = 4;
      const showMoreLimit = Math.max(
        1,
        Number.parseInt(this.getAttribute('data-show-more-limit'), 10) || 8
      );
      const visibleRows = Math.ceil(showMoreLimit / columnsPerRow);

      this.querySelectorAll('.bfcm-tab-products__products-container-inner .swiper-wrapper').forEach((e) => {
        if (e.classList.contains('need-show-more')) {
          const items = Array.from(e.children);
          const itemNum = e.childElementCount;
          const rowNum = Math.ceil(itemNum / columnsPerRow);
          const parent = e.parentElement;
          const showMoreBtn = parent.querySelector('.bfcm-tab-products__show-more-btn');

          const itemBoxNormal = document.createElement('div');
          const itemBoxExpand = document.createElement('div');
          e.innerHTML = '';
          itemBoxNormal.className = 'normal-box';
          itemBoxNormal.style.setProperty('grid-template-rows', `repeat(${visibleRows}, 1fr)`);

          itemBoxExpand.className = 'expand-box';
          itemBoxExpand.style.setProperty('grid-template-rows', `repeat(${Math.max(0, rowNum - visibleRows)}, 1fr)`);

          items.forEach((item, index) => {
            if (index < showMoreLimit) {
              itemBoxNormal.appendChild(item);
            } else {
              itemBoxExpand.appendChild(item);
            }
          });
          e.appendChild(itemBoxNormal);
          e.appendChild(itemBoxExpand);

          if (itemNum <= showMoreLimit) {
            showMoreBtn.style.setProperty('display', 'none');
            itemBoxNormal.style.setProperty('grid-template-rows', `repeat(${rowNum}, 1fr)`);
          }

          showMoreBtn.addEventListener('click', () => {
            e.classList.toggle('collapsed');
            e.classList.toggle('expanded');
          });
        }
      });

      requestAnimationFrame(() => this.finishShowMoreLayout());
    }
  }

  customElements.define('common-tab-products', TabProducts);
}


if (!customElements.get('bfcm-tab-products-swiper')) {
  class BfcmTabProductsSwiper extends HTMLElement {
    constructor() {
      super();
      this._swiperInstance = null;
      this.columnsDesktop = Number(this.getAttribute("data-columns-desktop") ?? 2);
      this._swiperContainer = this;

      const isMobile = `(max-width: 768px)`;
      const isMobileMQL = window.matchMedia(isMobile);

      isMobileMQL.addEventListener('change', (event) => {
        if (!event.matches) {
          this.createSlider();
        } else if (this.mySwiper) {
          this.destroySwiper();
        }
      });
      if (!isMobileMQL.matches) this.createSlider();
    }

    createSlider() {
      const nextButton = this.querySelector('.swiper-btn-next');
      const prevButton = this.querySelector('.swiper-btn-prev');
      const pagination = this.querySelector(`.swiper-pagination`);

      this._swiperInstance = new Swiper(this._swiperContainer, {
        slidesPerView: 3,
        slidesPerGroup: 1,
        spaceBetween: 20,
        loop: false,
        direction: "horizontal",
        navigation: {
          nextEl: nextButton,
          prevEl: prevButton,
        },
        pagination: {
          el: pagination,
          clickable: true,
        },
        breakpoints: {
          769: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          990: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          1200: {
            slidesPerView: 2.5,
            spaceBetween: 20,
          },
        }
      });
    }

    destroySwiper() {
      this._swiperInstance.destroy();
    }
  }

  customElements.define('bfcm-tab-products-swiper', BfcmTabProductsSwiper);
}